import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null, showDetails: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleCopyError = () => {
    const errorDetails = `
Error: ${this.state.error?.toString()}
Component Stack: ${this.state.errorInfo?.componentStack}
User Agent: ${navigator.userAgent}
URL: ${window.location.href}
Time: ${new Date().toISOString()}
    `.trim();

    navigator.clipboard.writeText(errorDetails);
    alert('Rapport d\'erreur copié dans le presse-papiers !');
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div 
          style={{
            maxWidth: '700px',
            margin: '40px auto',
            padding: '40px 24px',
            background: 'var(--card-bg, #ffffff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '24px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
            textAlign: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >
          <div 
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '20px',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <i className="material-icons" style={{ fontSize: '32px' }}>error_outline</i>
          </div>

          <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-heading, #1e293b)', marginBottom: '12px' }}>
            Oups, un problème est survenu
          </h2>
          
          <p style={{ fontSize: '15px', color: 'var(--text-muted, #64748b)', lineHeight: '1.6', maxWidth: '500px', margin: '0 auto 30px' }}>
            Désolé, une erreur technique inattendue s'est produite lors de l'affichage de ce composant. L'équipe a été notifiée.
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '32px' }}>
            <button
              onClick={this.handleReload}
              style={{
                background: '#6366f1',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '14px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#4f46e5')}
              onMouseOut={(e) => (e.currentTarget.style.background = '#6366f1')}
            >
              <i className="material-icons" style={{ fontSize: '18px' }}>refresh</i>
              Actualiser la page
            </button>

            <button
              onClick={this.handleCopyError}
              style={{
                background: 'transparent',
                color: 'var(--text-heading, #1e293b)',
                border: '1px solid var(--border-color, #e2e8f0)',
                padding: '12px 24px',
                borderRadius: '14px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'var(--bg-body, #f8fafc)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <i className="material-icons" style={{ fontSize: '18px' }}>content_copy</i>
              Copier l'erreur
            </button>
          </div>

          <div style={{ textAlign: 'left', borderTop: '1px solid var(--border-color, #e2e8f0)', paddingTop: '20px' }}>
            <button
              onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
              style={{
                background: 'none',
                border: 'none',
                color: '#6366f1',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: 0,
              }}
            >
              <i className="material-icons" style={{ fontSize: '16px' }}>
                {this.state.showDetails ? 'expand_less' : 'expand_more'}
              </i>
              {this.state.showDetails ? 'Masquer les détails techniques' : 'Afficher les détails techniques'}
            </button>

            {this.state.showDetails && (
              <div 
                style={{
                  marginTop: '16px',
                  background: 'var(--bg-body, #f8fafc)',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '12px',
                  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
                  color: '#e11d48',
                  overflowX: 'auto',
                  maxHeight: '200px',
                  lineHeight: '1.5'
                }}
              >
                <strong>{this.state.error?.toString()}</strong>
                {this.state.errorInfo && (
                  <pre style={{ margin: '8px 0 0', color: 'var(--text-muted, #64748b)', whiteSpace: 'pre-wrap' }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
